<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import logger from '@/utils/logger'
import ConfirmDialog from '@/components-v4/cancel-button/confirm-dialog.vue'
import QuestionnaireContent from '@/components-v4/cancel-button/questionnaire-content.vue'
import CancelDialogCommonContent from '@/components-v4/cancel-button/common-content.vue'
import UserFeedbackMixin from '@/mixins/user-feedback'
import CHECKOUT_STEP from '@/constants/checkout-step'
import UQUDO_VARIANTS from '@/constants/uqudo-variants'
import { hackyGetUqudoStep } from '@/utils/uqudo'
import { TRACKING_EVENTS } from '@/constants/tracking'

import CancelButtonMixin from './cancel-button-mixin'

export default {
  name: 'CancelButton',
  components: {
    CancelDialogCommonContent,
    QuestionnaireContent,
    ConfirmDialog,
  },
  mixins: [UserFeedbackMixin, CancelButtonMixin],
  props: {
    disabled: Proptypes.boolean(false),
  },
  data() {
    return {
      fetchingAnswer: false,
      feedbackAnswerId: '',
      feedbackComment: '',
      needToConfirmCancellation: true,
    }
  },
  computed: {
    ...mapState('application', ['direction']),
    ...mapGetters('userFeedback', ['feedbackQuestion', 'answerOptions']),
    ...mapGetters('session', ['merchantName', 'singleCheckoutRevampEnabled']),
    disabledConfirmToLeaveButton() {
      return this.isRequiredAnswer && !this.feedbackAnswerId
    },
    isUqudoIdvStep() {
      return this.currentStep === CHECKOUT_STEP.ID_VERIFICATION_UQUDO
    },
    checkoutBackLocation() {
      const query = JSON.parse(
        window.storage.getItem(UQUDO_VARIANTS.CHECKOUT_QUERY)
      )
      return {
        path: '/checkout',
        query,
      }
    },
    feedbackQuestionDescription() {
      return this.$t('user_feedback_question_description')
    },
  },
  watch: {
    showConfirm(newValue) {
      if (newValue && this.isEnabledUserCheckoutFeedback) {
        this.fetchQuestion()
      }
    },
  },
  methods: {
    ...mapActions('session', ['cancelCheckoutSession']),
    ...mapActions('userFeedback', [
      'fetchFeedbackAnswers',
      'submitUserFeedback',
    ]),

    async fetchQuestion() {
      try {
        this.fetchingAnswer = true

        await this.fetchFeedbackAnswers({
          questionCode: this.feedbackQuestionCode,
        })

        this.initFeedbackAnswer()
      } catch (err) {
        logger.error(err, {}, { category: 'feedback' })
      } finally {
        this.fetchingAnswer = false
      }
    },
    initFeedbackAnswer() {
      this.feedbackAnswerId = ''
    },
    onClosedDialog() {
      this.trackEvent(TRACKING_EVENTS.CANCEL_ORDER_POPUP_CLOSED.EVENT_NAME)
      this.resetData()
    },
    onCloseClicked() {
      this.trackEvent(TRACKING_EVENTS.CANCEL_ORDER_POPUP_CLOSED.EVENT_NAME)
      this.resetData()
      this.hiddePopup()
    },
    onConfirmedCancel() {
      if (
        this.isEnabledUserCheckoutFeedback &&
        this.needToConfirmCancellation
      ) {
        this.needToConfirmCancellation = false

        return
      }

      this.trackEvent(
        TRACKING_EVENTS.CANCEL_ORDER_POPUP_BACK_TO_MERCHANT_CLICKED.EVENT_NAME,
        {
          feedback_answer_id: this.feedbackAnswerId,
          feedback_comments: this.feedbackComment,
        }
      )

      this.hiddePopup()
      if (this.isEnabledUserCheckoutFeedback) {
        this.handleSubmitFeedback()
      }
      if (this.isUqudoIdvStep) {
        this.$emit('confirmClose', 'EXIT', {
          uqudo_step: hackyGetUqudoStep(),
        })
      }

      this.processCancelOrder()
    },
    onCancelledAction() {
      this.trackEvent(
        TRACKING_EVENTS.CANCEL_ORDER_POPUP_CONTINUE_WITH_TAMARA_CLICKED
          .EVENT_NAME,
        {
          feedback_answer_id: this.feedbackAnswerId,
          feedback_comments: this.feedbackComment,
        }
      )
      this.resetData()
    },
    async handleSubmitFeedback() {
      try {
        await this.submitUserFeedback({
          step: this.userFeedbackStep,
          answerId: this.feedbackAnswerId,
          comment: this.feedbackComment,
        })
      } catch (_) {
        // Do nothing. We just catch error. This action is optional, so we don't need to handle error
      }
    },
    resetData() {
      // Reset all data that user selected
      this.showConfirm = false
      this.fetchingAnswer = false
      this.feedbackAnswerId = ''
      this.feedbackComment = ''
      this.needToConfirmCancellation = true

      // Reset child component's state.
      if (this.$refs.questionnaireContentRef) {
        this.$refs.questionnaireContentRef.resetData()
      }
    },
  },
}
</script>

<template>
  <div class="tamara-cancel-button-template" @click="showPopup">
    <slot />
    <ConfirmDialog
      :visible="showConfirm"
      :show-close-button="true"
      :persistent="false"
      @click:close="onCloseClicked"
      @confirm="onConfirmedCancel"
      @cancel="onCancelledAction"
      @close="onClosedDialog"
    >
      <template v-slot="{ on }">
        <QuestionnaireContent
          v-if="!needToConfirmCancellation"
          ref="questionnaireContentRef"
          :selected-answer-id.sync="feedbackAnswerId"
          :comment.sync="feedbackComment"
          :question="feedbackQuestion"
          :question-description="feedbackQuestionDescription"
          :answers="answerOptions"
          :direction="direction"
          :fetching-answer="fetchingAnswer"
          :disabled-confirm-button="disabledConfirmToLeaveButton"
          :merchant-name="merchantName"
          :show-continue-with-tamara-button="!singleCheckoutRevampEnabled"
          v-on="on"
        />
        <CancelDialogCommonContent
          v-else
          :title="$t('confirm_leaving_title')"
          :message="$t('confirm_leaving_message')"
          :confirm-text="$t('text_leave')"
          :cancel-text="$t('text_stay')"
          v-on="on"
        />
      </template>
    </ConfirmDialog>
  </div>
</template>

<style lang="scss">
.cancel-button-dialog__confirm-btn {
  border: none !important;
}

.tamara-application--enabled-single-checkout-revamp.is-mobile {
  .v-dialog__content {
    align-items: flex-end;
    z-index: 2030;

    .v-dialog {
      margin: 0 !important;
      max-width: 100vw !important;
      width: 100vw !important;
      border-radius: 0 !important;
    }
  }
}
</style>
